import { bind, classNames, h, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { useSchedule as useScheduleV1 } from "@/components/nw-nav/store/queries/schedule";
import { useSchedule as useScheduleV2 } from "@/pages/project/features/directory/store/queries/useSchedule";
import { PureComponent } from "@/utils/pureComponent";
import { Task, TaskStatus } from "@/api";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { BarSegment } from "./components/bar-segment";

export const enum ProgressState {
  NotStarted = "not-started",
  InProgress = "in-progress",
  InReview = "in-review",
  Complete = "complete",
}

type Props = {};

@tag("nw-progressbar")
export class NWProgressBar extends PureComponent<Props> {
  static css = [tailwind];

  private getStateFromTask(task: Task): ProgressState {
    if (task.status === TaskStatus.TASK_COMPLETE) {
      return ProgressState.Complete;
    }

    if (task.id === Store.project.id.value) {
      return ProgressState.InProgress;
    }

    if (task.status === TaskStatus.TASK_IN_REVIEW) {
      return ProgressState.InReview;
    }

    return ProgressState.NotStarted;
  }

  @bind
  private isActiveTask(task: Task) {
    return task.id ? task.id === Store.project.id.value : false;
  }

  @bind
  private createTaskFromCurrentProject(): Task {
    const metadata = Store.project.metadata.value!;

    return {
      id: Store.project.id.value!,
      name: metadata.title!,
      description: metadata.description!,
      type: "project",
      status: Store.project.status.value!,
      category: metadata.category!,
    };
  }

  @bind
  private getTasks() {
    const tasks = Store.features.hasFeature("projects.navV2")
      ? useScheduleV2().getDaysTasks()
      : useScheduleV1().getDaysTasks();
    return isEmpty(tasks) ? [this.createTaskFromCurrentProject()] : tasks;
  }

  render() {
    const isActive = Store.app.isRouteActive("project") && !Store.app.isNavOpen.value;
    const tasks = this.getTasks();
    const isLoading = Store.features.hasFeature("projects.navV2")
      ? useScheduleV2().isLoading.value
      : useScheduleV1().isLoading.value;

    return (
      <div
        class={classNames(
          "bg-white transition-all flex items-center py-3 px-6 top-0 left-0 right-0 z-20 shrink-0 h-10 space-x-2 sm:space-x-4 w-full peer",
          { "opacity-0": isLoading, "sticky animate-slideDown opacity-100": !isLoading },
          { "!opacity-0": !isActive, "!opacity-100": isActive },
        )}
      >
        {tasks?.map((task, idx) => (
          <BarSegment
            class={classNames({ hidden: isLoading })}
            task={task}
            isActive={this.isActiveTask(task)}
            state={this.getStateFromTask(task)}
            last={tasks.length > 3 && idx === tasks.length - 1}
            first={tasks.length > 3 && idx === 0}
            single={tasks.length === 1}
          />
        ))}
      </div>
    );
  }
}
